'use strict';
const options_collection = {
  collectionsLoadMore: '.collections-load-more',
  rangeSlider: '.price-range .range-slider',
  rangeSliderMin: '.range-slider-min',
  rangeSliderMax: '.range-slider-max',
  minPriceRange: '.min-price-range',
  maxPriceRange: '.max-price-range',
  collectionSidebar: '#CollectionSidebar',
  collectionFilterBlocks: '.filter-blocks',
  productgridcontainer: '#productgridcontainer',
  collectionFiltersForm: '#CollectionFiltersForm',
  section: '.sec__collection-main',
};

function vskloFormatPriceRangeMoney(cents) {
  const safeCents = Math.max(0, Math.round(Number(cents || 0)));

  if (typeof window.vskloFormatMoney === 'function') {
    return window.vskloFormatMoney(safeCents);
  }

  const moneyFormat =
    window.themeGlobalVariables?.settings?.money_format ||
    window.cartStrings?.money_format ||
    '';

  if (window.Shopify?.formatMoney && moneyFormat) {
    return window.Shopify.formatMoney(safeCents, moneyFormat);
  }

  const currency =
    window.VSKLO_STOREFRONT?.currency ||
    window.Shopify?.currency?.active ||
    window.vskloPresentmentCurrency ||
    '';
  const locale = document.documentElement.lang || undefined;
  const amount = safeCents / 100;

  if (currency && window.Intl && Intl.NumberFormat) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {}
  }

  return amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* The filter is a viewport dialog. Keep it under <body> while open so a
   sticky header or a section transform cannot cover its title and close button.
   Restore its original section slot when closed. AJAX may replace the dialog
   node while it is open, so always resolve it by ID. */
let vskloFilterPortalAnchor = null;
let vskloFilterReturnFocus = null;
let vskloFilterCloseTimer = null;
function vskloFilterActsAsDrawer(sidebar) {
  // Most VSKLO collections are "vertical" on desktop but use the same drawer on mobile.
  return !!sidebar && (sidebar.classList.contains('drawer') ||
    !!window.matchMedia?.('(max-width: 1024px)').matches);
}
function vskloPortalCollectionFilter(sidebar) {
  if (!vskloFilterActsAsDrawer(sidebar)) return;
  if (!vskloFilterPortalAnchor && sidebar.parentNode && sidebar.parentNode !== document.body) {
    vskloFilterPortalAnchor = document.createComment('VSKLO filter drawer source position');
    sidebar.parentNode.insertBefore(vskloFilterPortalAnchor, sidebar);
  }
  if (sidebar.parentNode !== document.body) document.body.appendChild(sidebar);
}
function vskloRestoreCollectionFilter(sidebar) {
  if (sidebar && vskloFilterPortalAnchor?.parentNode) {
    vskloFilterPortalAnchor.parentNode.insertBefore(sidebar, vskloFilterPortalAnchor);
  }
  vskloFilterPortalAnchor?.remove();
  vskloFilterPortalAnchor = null;
}
// In horizontal collections the sidebar's home lives INSIDE the AJAX grid.
// A grid refresh replaces the original portal anchor and inserts a duplicate
// sidebar node. Turn that duplicate into the new anchor before the old dialog
// is queried again, preserving its DOM, open state and next close position.
function vskloReanchorCollectionFilter(duplicateSidebar) {
  if (!duplicateSidebar || duplicateSidebar.id !== 'CollectionSidebar') return false;
  const anchor = document.createComment('VSKLO filter drawer source position');
  duplicateSidebar.replaceWith(anchor);
  vskloFilterPortalAnchor?.remove();
  vskloFilterPortalAnchor = anchor;
  return true;
}
window.VSKLOReanchorCollectionFilter = vskloReanchorCollectionFilter;

function vskloEnsureCollectionScrim() {
  let scrim = document.getElementById('vsklo-collection-filter-scrim');
  if (!scrim) {
    scrim = document.createElement('button');
    scrim.id = 'vsklo-collection-filter-scrim';
    scrim.type = 'button';
    scrim.tabIndex = -1;
    scrim.setAttribute('aria-label', document.querySelector('close-filter')?.getAttribute('aria-label') || 'Close filter');
    scrim.addEventListener('click', () => vskloSetCollectionFiltersOpen(false));
    document.body.appendChild(scrim);
  }
  return scrim;
}

/* Single source of truth for the collection drawer. Never cache the sidebar:
   canonical AJAX faceting can replace #CollectionSidebar after every request. */
function vskloSetCollectionFiltersOpen(shouldOpen, options = {}) {
  // A new user action can reverse an unfinished closing transition.
  if (vskloFilterCloseTimer !== null) {
    window.clearTimeout(vskloFilterCloseTimer);
    vskloFilterCloseTimer = null;
  }
  const sidebar = document.getElementById('CollectionSidebar');
  const html = document.documentElement;
  const open = Boolean(shouldOpen && sidebar);
  const wasOpen = Boolean(sidebar && sidebar.classList.contains('open'));
  const animateClose = Boolean(!open && wasOpen && vskloFilterActsAsDrawer(sidebar) &&
    options.animate !== false && window.matchMedia?.('(prefers-reduced-motion: no-preference)')?.matches);

  if (open) {
    sidebar.classList.remove('vsklo-filter-exiting');
    document.getElementById('vsklo-collection-filter-scrim')?.classList.remove('vsklo-filter-exiting');
    if (!options.preserveScroll && !vskloFilterReturnFocus?.isConnected) {
      vskloFilterReturnFocus = document.activeElement;
    }
    vskloEnsureCollectionScrim();
    vskloPortalCollectionFilter(sidebar);
  }
  if (sidebar) {
    sidebar.classList.toggle('open', open);
    if (vskloFilterActsAsDrawer(sidebar)) {
      sidebar.setAttribute('role', 'dialog');
      sidebar.setAttribute('aria-modal', String(open));
      sidebar.setAttribute('aria-hidden', String(!open));
    }
  }
  html.classList.toggle('open-sidebar', open);

  if (open) {
    // A mobile sort sheet and the facet drawer cannot both own a backdrop.
    document.querySelectorAll('.toolbar-sorter .select-custom.active').forEach((menu) => menu.classList.remove('active'));
    html.classList.add('open-drawer');
    window.VSKLOOverlayState?.acquire?.('collection-sidebar');

    if (!html.style.getPropertyValue('padding-right') &&
        typeof getScrollBarWidth !== 'undefined' &&
        typeof getScrollBarWidth?.init === 'function') {
      html.style.setProperty('padding-right', getScrollBarWidth.init() + 'px');
    }
    if (!options.preserveScroll) {
      const blocks = sidebar.querySelector('.filter-blocks');
      if (blocks) blocks.scrollTop = 0;
      sidebar.querySelector('close-filter')?.focus?.({ preventScroll: true });
    }
  } else {
    if (animateClose) {
      // Leave the original sidebar portaled until the 260ms exit completes.
      // Release scroll/overlay state below without delaying user navigation.
      const closingSidebar = sidebar;
      const closingScrim = document.getElementById('vsklo-collection-filter-scrim');
      closingSidebar.classList.add('vsklo-filter-exiting');
      closingScrim?.classList.add('vsklo-filter-exiting');
      vskloFilterCloseTimer = window.setTimeout(() => {
        vskloFilterCloseTimer = null;
        if (closingSidebar.classList.contains('open')) return;
        closingSidebar.classList.remove('vsklo-filter-exiting');
        if (closingSidebar.isConnected && document.getElementById('CollectionSidebar') === closingSidebar) {
          vskloRestoreCollectionFilter(closingSidebar);
        } else {
          vskloRestoreCollectionFilter(null);
        }
        closingScrim?.remove();
      }, 270);
    } else {
      sidebar?.classList.remove('vsklo-filter-exiting');
      vskloRestoreCollectionFilter(sidebar);
      document.getElementById('vsklo-collection-filter-scrim')?.remove();
    }
    if (wasOpen) {
      // The AJAX grid refresh can replace the original toolbar trigger.
      // Restore focus to the current trigger rather than leaving it on a
      // detached DOM node after close/Apply/Escape.
      const returnTarget = vskloFilterReturnFocus?.isConnected
        ? vskloFilterReturnFocus
        : document.querySelector('.sec__collection-main btn-filter');
      returnTarget?.focus?.({ preventScroll: true });
    }
    vskloFilterReturnFocus = null;
    html.classList.remove('hside_opened');
    window.VSKLOOverlayState?.release?.('collection-sidebar');
    if (typeof window.VSKLOCleanupSharedDrawerShell === 'function') {
      window.VSKLOCleanupSharedDrawerShell();
    } else if (!html.classList.contains('open-minicart') &&
               !html.classList.contains('open-search') &&
               !html.classList.contains('nav-open') &&
               !html.classList.contains('vsklo-mobile-drawer-open')) {
      html.classList.remove('open-drawer');
      html.style.removeProperty('padding-right');
    }
  }

  // More than one filter trigger may exist in theme markup. They always agree.
  document.querySelectorAll('btn-filter').forEach((button) => {
    button.classList.toggle('open', open);
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-controls', 'CollectionSidebar');
  });

  window.VSKLOOverlayState?.reconcile?.();
  window.vskloSyncOverlayState?.();
  return open;
}
window.VSKLOSetCollectionFiltersOpen = vskloSetCollectionFiltersOpen;
window.VSKLOCloseCollectionFilters = () => vskloSetCollectionFiltersOpen(false);

class BtnFilter extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onOpen.bind(this), false);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.onOpen();
    });
  }
  onOpen() {
    const sidebar = document.getElementById('CollectionSidebar');
    if (!sidebar) {
      vskloSetCollectionFiltersOpen(false);
      return;
    }
    if (!sidebar.classList.contains('open')) vskloFilterReturnFocus = this;
    vskloSetCollectionFiltersOpen(!sidebar.classList.contains('open'));
  }
}
customElements.define('btn-filter', BtnFilter);

class CloseFilter extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClose.bind(this), false);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.onClose();
    });
  }
  onClose() {
    vskloSetCollectionFiltersOpen(false);
  }
}
customElements.define('close-filter', CloseFilter);

class FacetApplyCanvas extends CloseFilter {}
customElements.define('facet-apply-canvas', FacetApplyCanvas);

// Escape and scrim dismissal must use the same state path as the close button.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const sidebar = document.getElementById('CollectionSidebar');
  if (!sidebar || !sidebar.classList.contains('open')) return;
  event.preventDefault();
  vskloSetCollectionFiltersOpen(false);
  document.querySelector('btn-filter')?.focus?.();
});
document.addEventListener('click', (event) => {
  const sidebar = document.getElementById('CollectionSidebar');
  if (!sidebar || !sidebar.classList.contains('open')) return;
  if (event.target.closest('#CollectionSidebar, btn-filter')) return;
  event.preventDefault();
  event.stopPropagation();
  vskloSetCollectionFiltersOpen(false);
}, true);

// Do not keep the filter backdrop/scroll lock across mobile-desktop changes.
const vskloFilterViewport = window.matchMedia?.('(max-width: 1024px)');
if (vskloFilterViewport) {
  const onFilterViewportChange = () => {
    if (document.getElementById('CollectionSidebar')?.classList.contains('open')) {
      vskloSetCollectionFiltersOpen(false, { animate: false });
    }
  };
  if (vskloFilterViewport.addEventListener) {
    vskloFilterViewport.addEventListener('change', onFilterViewportChange);
  } else if (vskloFilterViewport.addListener) {
    vskloFilterViewport.addListener(onFilterViewportChange);
  }
}

/* Grid layout is an immediate CSS state change, not a staggered animation.
   Previously every card waited for Motion.stagger on both the exit and entry,
   causing seconds of apparent UI lag in large Shopify collections. */
class GridMode extends HTMLElement {
  constructor() {
    super();
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-pressed', String(this.classList.contains('active')));
    this.addEventListener('click', () => this.gridMode());
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.gridMode();
    });
  }
  gridMode() {
    if (this.classList.contains('active')) return;
    const mode = Number(this.getAttribute('data-grid-mode'));
    const container = document.querySelector('.container-products-switch');
    if (!container || ![2, 3, 4].includes(mode)) return;

    const current = Number(container.getAttribute('data-view-mode'));
    if ([2, 3, 4].includes(current)) container.classList.remove('grid-columns-' + current);
    container.setAttribute('data-view-mode', String(mode));
    container.classList.add('grid-columns-' + mode);

    document.querySelectorAll('grid-mode').forEach((button) => {
      const active = button === this;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }
}
customElements.define('grid-mode', GridMode);

class FilterItem extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.filterItem.bind(this), false);
  }
  filterItem() {
    if (window.__VSKLO_FILTER_FAST_V8__) return;
    if (this.querySelector('.tooltip')) {
      this.querySelector('.tooltip').classList.toggle('current-filter');
    } else {
      if (this.closest('li')) {
        this.closest('li').classList.toggle('current-filter');
      }
    }
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
  }
}
customElements.define('filter-item', FilterItem);

class FacetClearAll extends CloseFilter {
  constructor() {
    super();
    this.addEventListener('click', this.filterItem.bind(this), false);
  }
  filterItem() {
    if (window.__VSKLO_FILTER_FAST_V8__) return;
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
  }
}
customElements.define('facet-clear-all', FacetClearAll);

class FilterSort extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.filterSort.bind(this), false);
  }
  filterSort() {
    if (window.__VSKLO_FILTER_FAST_V8__) return;
    const value = this.getAttribute('value');
    let queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('sort_by', queryString);
    const searchParams = queryString.replace('?', '') + '&sort_by=' + value;
    const url = eventCollectionShopify.renderUrl(searchParams);
    eventCollectionShopify.renderSectionFilter(url, searchParams);
  }
}
customElements.define('filter-sort', FilterSort);

class SelectSorter extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.activeFilterSort.bind(this), false);
  }
  activeFilterSort() {
    const select = this.closest('.select-custom');
    if (!select) return;
    select.classList.toggle('active');
  }
}
customElements.define('select-sorter', SelectSorter);

var eventCollectionShopify = (function () {
  return {
    init: function () {
      this.ionRangeSlider();
      let windowWidth = window.innerWidth;
      if (windowWidth <= 1024) {
        const horizontal = document.querySelector('.horizontal-filter');
        if (horizontal) {
          const collapsible = document.querySelectorAll('collapsible-block');
          collapsible.forEach((e) => {
            e.classList.add('active');
          });
        }
      }
    },

    ionRangeSlider: function () {
      // VSKLO price slider V7 has a single dedicated owner in
      // vsklo-collection-filter.js. Keep the theme collection runtime
      // responsible for sorting/grid/drawer only to avoid duplicate
      // price listeners and duplicate AJAX requests.
      if (document.querySelector('#CollectionSidebar .vsklo-price-filter')) return;

      const price_range = document.querySelector('.price-range');
      const maxRange = document.querySelector(
        options_collection.rangeSliderMax
      );
      const minRange = document.querySelector(
        options_collection.rangeSliderMin
      );
      const minPrice = document.querySelector('.min-price-range');
      const maxPrice = document.querySelector('.max-price-range');
      const filtersForm = document.querySelector(options_collection.collectionFiltersForm);
      const minLabel = document.querySelector('.price-lable .min');
      const maxLabel = document.querySelector('.price-lable .max');
      var minPriceRange = 0;
      var maxPriceRange = 0;
      if (!price_range || !maxRange || !minRange || !minPrice || !maxPrice || !filtersForm) return;
      if (price_range.dataset.vskloLegacyPriceBound === 'true') return;
      price_range.dataset.vskloLegacyPriceBound = 'true';
      minRange.addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(minRange.min) + Number(target.value) >= 0) {
            if (Number(target.value) > Number(maxRange.max)) {
              minPrice.value = Number(maxRange.max);
            } else {
              minPrice.value = Number(target.value);
            }
          } else {
            minPrice.value = Number(minRange.min);
          }
          const formData = new FormData(filtersForm);
          const params = new URLSearchParams(formData).toString();
          const nameMin = minPrice.getAttribute('name');
          const nameMax = maxPrice.getAttribute('name');
          var queryString = window.location.search;
          queryString = this.removeParam(nameMin, queryString);
          queryString = this.removeParam(nameMax, queryString);
          var searchParams = queryString.replace('?', '') + '&' + params;
          const url = this.renderUrl(searchParams);
          this.renderSectionFilter(url, searchParams);
        },
        false
      );
      maxRange.addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(maxRange.max) - Number(target.value) >= 0) {
            maxPrice.value = Number(target.value);
          } else {
            maxPrice.value = Number(maxRange.max);
          }
          const formData = new FormData(filtersForm);
          const params = new URLSearchParams(formData).toString();
          const nameMin = minPrice.getAttribute('name');
          const nameMax = maxPrice.getAttribute('name');
          var queryString = window.location.search;
          queryString = this.removeParam(nameMin, queryString);
          queryString = this.removeParam(nameMax, queryString);
          var searchParams = queryString.replace('?', '') + '&' + params;
          const url = this.renderUrl(searchParams);
          this.renderSectionFilter(url, searchParams);
        },
        false
      );
      minRange.addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (Number(maxRange.value) - Number(target.value) >= 1) {
              minPrice.value = Number(target.value);
              minPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(maxRange.value) - 1;
              minPriceRange = (Number(maxRange.value) - 1) * 100;
            }
            const price_format = vskloFormatPriceRangeMoney(minPriceRange);
            if (minLabel) minLabel.innerHTML = price_format;
            const rangeHost = target.closest('.price-range');
            if (rangeHost) rangeHost.style.setProperty(
                '--range-from',
                (minRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      maxRange.addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (target.value - minRange.value >= 1) {
              maxPrice.value = target.value;
              maxPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(minRange.value) + Number(1);
              maxPriceRange = (Number(minRange.value) + Number(1)) * 100;
            }
            const price_format = vskloFormatPriceRangeMoney(maxPriceRange);
            if (maxLabel) maxLabel.innerHTML = price_format;
            const rangeHost = target.closest('.price-range');
            if (rangeHost) rangeHost.style.setProperty(
                '--range-to',
                (maxRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      document
        .querySelectorAll(options_collection.rangeSlider)
        .forEach((sliderRange) => {
          sliderRange.addEventListener(
            'change',
            (event) => {
              event.preventDefault();
              const target = event.currentTarget;
              const formData = new FormData(filtersForm);
              const params = new URLSearchParams(formData).toString();
              const nameMin = minPrice.getAttribute('name');
              const nameMax = maxPrice.getAttribute('name');
              var queryString = window.location.search;
              queryString = this.removeParam(nameMin, queryString);
              queryString = this.removeParam(nameMax, queryString);
              var searchParams = queryString.replace('?', '') + '&' + params;
              const url = this.renderUrl(searchParams);
              this.renderSectionFilter(url, searchParams);
            },
            false
          );
        });
    },

    removeParam: function (key, sourceURL) {
      var rtn = sourceURL.split('?')[0],
        param,
        params_arr = [],
        queryString =
          sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';
      if (queryString !== '') {
        params_arr = queryString.split('&');
        for (var i = params_arr.length - 1; i >= 0; i -= 1) {
          param = params_arr[i].split('=')[0];
          if (param === key) {
            params_arr.splice(i, 1);
          }
        }
        if (params_arr.length) rtn = rtn + '?' + params_arr.join('&');
      }
      return rtn;
    },

    renderUrl: function (searchParams) {
      const section = document.querySelector(options_collection.section);
      const sectionId = section?.dataset?.sectionId;
      if (!sectionId) return '';
      return `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
    },

    renderSectionFilter: function (url, searchParams) {
      const normalizedQuery = this.removeParam('page', `?${searchParams || ''}`);
      searchParams = normalizedQuery.replace(/^\?/, '');
      url = this.renderUrl(searchParams);
      if (!url) return;
      this.toggleLoading(document.body, true);
      if (this._filterAbortController) {
        this._filterAbortController.abort();
      }

      const controller = new AbortController();
      this._filterAbortController = controller;
      let rendered = false;

      fetch(`${url}`, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }
          return response.text();
        })
        .then((responseText) => {
          const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
          const isMobile =
            typeof window.VSKLOIsMobile === 'function'
              ? window.VSKLOIsMobile()
              : window.innerWidth <= 1024;

          if (isMobile) {
            const collectionFilterBlocks = responseDocument.querySelector(
              options_collection.collectionFilterBlocks
            );
            const productgridcontainer = responseDocument.querySelector(
              options_collection.productgridcontainer
            );
            const currentFilterBlocks = document.querySelector(
              options_collection.collectionFilterBlocks
            );
            const currentProductGrid = document.querySelector(
              options_collection.productgridcontainer
            );

            if (!collectionFilterBlocks || !productgridcontainer || !currentFilterBlocks || !currentProductGrid) {
              throw new Error('Collection filter response is incomplete');
            }

            currentFilterBlocks.innerHTML = collectionFilterBlocks.innerHTML;
            currentProductGrid.innerHTML = productgridcontainer.innerHTML;

            // Legacy fallback must never dismiss the filter after a facet click.
          } else {
            const newSection = responseDocument.querySelector(options_collection.section);
            const currentSection = document.querySelector(options_collection.section);
            if (!newSection || !currentSection) {
              throw new Error('Collection section response is incomplete');
            }
            currentSection.replaceWith(newSection);
          }

          // Legacy fallback must never dismiss the filter after a facet click.

          this.updateUrl(url, searchParams);
          rendered = true;

          const updatedSection = document.querySelector(options_collection.section);
          document.dispatchEvent(new CustomEvent('vsklo:content-updated', {
            detail: { scope: updatedSection || document }
          }));

          const sectionElement = document.querySelector('#productgridcontainer');
          if (sectionElement && !document.getElementById('CollectionSidebar')?.classList.contains('open')) {
            const elementPosition = sectionElement.getBoundingClientRect().top;
            const offsetPosition = window.pageYOffset;
            const extraOffset = 100;
            const scrollToPosition = elementPosition + offsetPosition - extraOffset;
            window.scrollTo({
              top: scrollToPosition,
              behavior: 'smooth',
            });
          }
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error('[VSKLO] Collection filter request failed:', error);
        })
        .finally(() => {
          if (this._filterAbortController === controller) {
            this._filterAbortController = null;
            this.toggleLoading(document.body, false);
          }

          if (!rendered) return;

          const motionCollection = document.querySelector(".motion-collection");
          if (motionCollection) {
            motionCollection.reloadAnimationEffect();
          }
          initLazyloadItem();
          BlsLazyloadImg.init();
          eventCollectionShopify.ionRangeSlider();
        });
    },

    toggleLoading: function (event, loading) {
      if (event) {
        if (loading) {
          event.classList.add('start', 'loading');
        } else {
          event.classList.add('finish');
          setTimeout(function () {
            event.classList.remove('start', 'loading', 'finish');
          }, 500);
        }
      }
    },

    updateUrl: function (url, searchParams = false) {
      if (searchParams) {
        const urlUpdate = `${window.location.pathname}?${searchParams}`;
        window.history.pushState({ url: urlUpdate }, '', urlUpdate);
      } else {
        window.history.pushState({ url: url }, '', url);
      }
    },
  };
})();
eventCollectionShopify.init();

window.addEventListener('pageshow', function (event) {
  if (!event || !event.persisted) return;
  vskloSetCollectionFiltersOpen(false);
});
